import { BrandOptionFields } from '../../../models/course/ICourseContent';
import styles from './course-modules.module.scss';
import Markdown from 'markdown-to-jsx';
import React, { useState } from 'react';
import useWindowResize from '../../../hooks/use-window-resize.hook';
import { deviceDetect } from '../../../data/device-detect';
import { Breakpoint } from '../../../constants/breakpoints';
import CourseModulesUS from '../course-modules-us/course-modules-us';
import CourseModulesUK from '../course-modules-uk/course-modules-uk-au';
import { IModule } from '../../../models/course/ICourseData';
import { groupBy } from '../../../utils/mapping-helpers/groupBy';

export type Props = {
  modules: IModule[];
  brandCountry: string;
  brandOptions: BrandOptionFields;
  degreeCredits?: string;
  classNotes?: string;
};

export default function CourseModules({
  modules,
  brandCountry,
  brandOptions,
  degreeCredits,
  classNotes,
}: Props) {
  const [isBreakpointUp, setIsBreakpointUp] = useState(!deviceDetect.isMobile);

  useWindowResize((newWidth) => setIsBreakpointUp(newWidth >= Breakpoint.Medium), 100);

  const groupedBySemester = brandCountry === 'US' && groupBy(modules, 'semesterTermTaught');

  const hasModulesDeliveredByInto = modules.some((module) => module.deliveredBy === 'INTO');

  return (
    <div className={styles.modules}>
      {degreeCredits ? (
        <div className={styles.degreeCredits}>
          <h5 className='modules-label'>Degree Program Components</h5>
          <div>
            {/*Removing the extra p tag with br.*/}
            <Markdown>
              {degreeCredits.replace('\u003Cp\u003E\u003Cbr\u003E\u003C/p\u003E', '')}
            </Markdown>
          </div>
        </div>
      ) : null}
      {/*TODO: Uncomment when we would have data for it  {instance.totalCreditInfo ? <Markdown options={{forceBlock: true}}>{fields.totalCreditInfo}</Markdown> : null}*/}
      {brandCountry === 'US' && Object.keys(groupedBySemester) ? (
        <div id='us-modules'>
          {isBreakpointUp ? (
            <div className='row mb-3'>
              <div className='col-md-3'>
                <h5>Credit Number</h5>
              </div>
              <div className='col-md-6 modules-label'>
                <h5>{brandOptions?.modulesName ? brandOptions.modulesName : 'Core'} Title</h5>
              </div>
              <div className='col-md-3 credits-label'>
                <h5>{brandOptions?.customizeCredits || 'Credit Value'}</h5>
              </div>
            </div>
          ) : null}
          {Object.keys(groupedBySemester).map((key) => (
            <CourseModulesUS
              brandOptions={brandOptions}
              modules={groupedBySemester[key]}
              semester={key}
              key={key}
            />
          ))}
        </div>
      ) : null}
      {brandCountry === 'GB' || brandCountry === 'AU' ? (
        <div id='uk-modules'>
          <div className='row mb-3'>
            <div className='col-md-9 modules-label'>
              <h5>
                Core{' '}
                {brandOptions?.modulesName ? brandOptions.modulesName.toLowerCase() : `modules`}
              </h5>
            </div>
            {isBreakpointUp ? (
              <div className='col-md-3 credits-label'>
                <h5>{brandOptions?.customizeCredits || 'Credit value'}</h5>
              </div>
            ) : null}
          </div>
          {modules.some((el) => el.type === 'Core') ? (
            <CourseModulesUK
              brandOptions={brandOptions}
              modules={modules.filter((el) => el.type === 'Core')}
            />
          ) : null}
          {modules.some((el) => el.type === 'Specialist') ? (
            <>
              <div className='row mb-3'>
                <div className='col-md-9 modules-label'>
                  <h5>
                    Specialist{' '}
                    {brandOptions?.modulesName ? brandOptions.modulesName.toLowerCase() : `modules`}
                  </h5>
                </div>
              </div>
              <CourseModulesUK
                brandOptions={brandOptions}
                modules={modules.filter((el) => el.type === 'Specialist')}
              />
            </>
          ) : null}
        </div>
      ) : null}

      <h5 className='modules-label'>Notes</h5>
      {brandCountry === 'US' ? (
        <ul>
          {hasModulesDeliveredByInto ? (
            <li id='delivered-by-into'>
              <span className={styles.into}>* </span>
              These courses are generally delivered through the INTO Center and are for Pathway
              students only. Students must maintain 2.0 GPA per semester to remain in good academic
              standing.
            </li>
          ) : null}
          {brandOptions?.classesNote && <li>{brandOptions.classesNote}</li>}
          {classNotes !== undefined ? (
            <li>
              <Markdown>{classNotes}</Markdown>
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
